import { useMemo } from 'react';
import { PolicyActionEnum } from '@trinity/access-policy';
import { useCan } from '@trinity/access-policy/react';

export type WidgetLibraryCapabilities = {
  /** Open the project's Widget Library tab. */
  canView: boolean;
  /** Add a widget to the library: save a chart, quote, media or tag summary, alone or on the way to a dashboard. */
  canAdd: boolean;
  /** Edit a saved widget. */
  canUpdate: boolean;
  /** Delete saved widgets, one at a time or in bulk. */
  canDelete: boolean;
};

/**
 * UI gating only: the project widget routes enforce the same actions (each write also needs View).
 * Reading library widgets is not gated, so dashboards keep rendering them.
 */
export const useWidgetLibraryCapabilities = (): WidgetLibraryCapabilities => {
  const canView = useCan(PolicyActionEnum.IeProjectViewWidgets);
  const canAddWidgets = useCan(PolicyActionEnum.IeProjectAddWidgets);
  const canUpdateWidgets = useCan(PolicyActionEnum.IeProjectUpdateWidgets);
  const canDeleteWidgets = useCan(PolicyActionEnum.IeProjectDeleteWidgets);

  return useMemo(
    () => ({
      canView,
      canAdd: canView && canAddWidgets,
      canUpdate: canView && canUpdateWidgets,
      canDelete: canView && canDeleteWidgets,
    }),
    [canView, canAddWidgets, canUpdateWidgets, canDeleteWidgets]
  );
};
